// src/components/map/basemaps.ts
/**
 * The keyless basemaps every ADMIN map draws from (MPPI-00507).
 *
 * Deliberately identical to `mppi-portal/src/components/map/basemaps.ts`. The
 * two SPAs are separate builds with separate dependency trees, so the file is
 * duplicated rather than shared — but the VALUES must not diverge, because the
 * operator's instruction was that the same map looks the same in both
 * products. Change one, change the other.
 *
 * Keyless is the requirement, not a convenience: a basemap that needs a
 * credential is a basemap that can silently stop rendering, which is exactly
 * what happened to the portal's Mapbox maps (MPPI-00490).
 *
 * CARTO is deliberately not used: it returns HTTP 200 without a key and paints
 * "API KEY REQUIRED" into the tile image itself, so no status check can detect
 * the failure (MPPI-00417).
 */
export type Basemap = "street" | "satellite" | "minimal";

export const TILES: Record<
  Basemap,
  { url: string; ref?: string; attribution: string }
> = {
  street: {
    url: "https://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
    attribution: "Esri, HERE, Garmin, &copy; OpenStreetMap contributors",
  },
  satellite: {
    url: "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    ref: "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
    attribution: "Esri, Maxar, Earthstar Geographics",
  },
  /**
   * The dashboard's dark canvas, for BUSY maps (MPPI-00507).
   *
   * A choropleth or a dense pin layer over coloured streets makes the reader
   * separate the data from the roads underneath; over a near-monochrome base
   * the data is the only thing with colour in it. That is why the dashboard
   * has always used this and why it now covers every data-dense map rather
   * than one.
   *
   * `ref` is the place-name layer, mounted ABOVE the data so labels are not
   * buried by the circles and polygons drawn on top of the base.
   */
  minimal: {
    url: "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    ref: "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
    attribution: "Esri, HERE, Garmin, &copy; OpenStreetMap contributors",
  },
};
